"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import donut from "@/public/animatedAssets/donut.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset2 = ({
  className = "",
  language,
}: {
  className?: string;
  language?: "en" | "de";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const gridVariants = {
    collapsed: {scale: 1, y: "-50%", x: "-50%", transition: ease},
    expanded: {scale: .7, y: "-50%", x: "-50%", transition: ease},
  }

  const assetContainerVariants = {
    collapsed: {height: "17.25rem", y: "-50%", x: "-50%", transition: ease},
    expanded: {height: "40rem", y: "-50%", x: "-50%", transition: ease},
  }

  const layer1Variants = {
    collapsed: {width: "31.25rem", height: "17.25rem", rotate: 0, transition: ease},
    expanded: {width: "28.5rem", height: "36.5rem", rotate: 45, transition: ease},
  }

  const layer2Variants = {
    collapsed: {width: "31.25rem", height: "17.25rem", rotate: 0, transition: ease},
    expanded: {width: "29.5rem", height: "36.5rem", rotate: 30, transition: ease},
  }

  const layer3Variants = {
    collapsed: {width: "31.25rem", height: "17.25rem", rotate: 0, transition: ease},
    expanded: {width: "30.5rem", height: "36.5rem", rotate: 15, transition: ease},
  }

  const layer4Variants = {
    collapsed: { height: "17rem", y: "-.25rem", transition: ease},
    expanded: { height: "40rem", y: 0, transition: ease},
  }

  const layer4AssetVariants = {
    collapsed: {opacity: 0, transition: ease},
    expanded: {opacity: 1, transition: ease},
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[55rem] lg:h-full bg-[#0A1A1D] relative overflow-clip",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center gap-[1rem]"
        variants={gridVariants}
        animate={controls}
        initial="collapsed"
      >
        <div className="w-[40rem] flex flex-col gap-[1rem]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[20.5rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}
        </div>

        <div className="w-[31.25rem] flex flex-col gap-[1rem]">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[20.5rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}

          <div className="w-full h-[17.25rem] border border-[#B9C0F9]/20 rounded-[1rem]" />

          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[20.5rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}
        </div>

        <div className="w-[40rem] flex flex-col gap-[1rem]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[20.5rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}
        </div>
      </motion.div>

      {/* center asset container */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[31.25rem] h-[17.25rem]"
        variants={assetContainerVariants}
        animate={controls}
        initial="collapsed"
      >
        <div className="relative w-full h-full flex items-center justify-center">
          <motion.div
            className="absolute w-[31.25rem] h-[17.25rem] rounded-[1rem] bg-[#B9C0F9]/10 backdrop-blur-[2rem]"
            variants={layer1Variants}
            animate={controls}
            initial="collapsed"
          />
          <motion.div
            className="absolute w-[31.25rem] h-[17.25rem] rounded-[1rem] bg-[#B9C0F9]/30 backdrop-blur-[2rem]"
            variants={layer2Variants}
            animate={controls}
            initial="collapsed"
          />
          <motion.div
            className="absolute w-[31.25rem] h-[17.25rem] rounded-[1rem] bg-[#B9C0F9]/50 backdrop-blur-[2rem]"
            variants={layer3Variants}
            animate={controls}
            initial="collapsed"
          />
          <motion.div
            className="absolute w-[31.25rem] h-[17rem] rounded-[1rem] bg-[#B9C0F9] p-[2rem]"
            variants={layer4Variants}
            animate={controls}
            initial="collapsed"
          >
            <div className="w-full h-full relative flex flex-col justify-between gap-[2rem]">
              <p className="mb-0 leading-none text-black/90 font-medium text-[2.25rem] relative z-10">{language === "en" ? "TRADING ANALYSIS" : "TRADING ANALYSE"}</p>

              <motion.div
                className="absolute top-1/2 left-1/2 -translate-y-[calc(50%+2rem)] -translate-x-1/2 w-[20rem] h-[20rem]"
                variants={layer4AssetVariants}
                animate={controls}
                initial="collapsed"
              >
                <div className="relative w-full h-full flex flex-col items-center justify-center">
                  <Image
                    className="!w-full absolute top-0 left-0 pointer-events-none"
                    src={donut}
                    alt=""
                  />
                  <p className="mb-[1rem] leading-none font-geistmono font-medium text-[4rem] text-white text-center">4930</p>
                  <p className="mb-0 leading-none font-geist font-medium text-[1.75rem] text-white/60 text-center">Total</p>
                </div>
              </motion.div>

              <p className="absolute z-10 bottom-0 left-0 right-0 mb-0 text-end leading-[.8] text-black/20 text-[8.25rem] font-medium">#01</p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};